import { useEffect } from "react";

const KennelSEO = ({ title, description, canonical, ogImage, ogType = "website", schema }) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description, "name");
    }
    setMeta("og:title", title, "property");
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary_large_image", "name");
    setMeta("twitter:title", title, "name");

    if (ogImage) {
      setMeta("og:image", ogImage, "property");
      setMeta("twitter:image", ogImage, "name");
    }

    if (canonical) {
      setMeta("og:url", canonical, "property");
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
      link.setAttribute("href", canonical);
    }

    if (schema) {
      let script = document.getElementById("kennel-jsonld");
      if (!script) { script = document.createElement("script"); script.id = "kennel-jsonld"; script.type = "application/ld+json"; document.head.appendChild(script); }
      script.textContent = JSON.stringify(Array.isArray(schema) ? schema : [schema]);
    }

    return () => {
      const s = document.getElementById("kennel-jsonld");
      if (s) s.remove();
    };
  }, [title, description, canonical, ogImage, schema]);

  return null;
};

const BASE = "https://wolfevomkatzenberg.pl";

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Wolfe vom Katzenberg - Hodowla Owczarka Niemieckiego",
  "description": "Profesjonalna hodowla owczarka niemieckiego zarejestrowana w ZKwP i FCI. Szczenieta z rodowodem, badania HD/ED, socjalizacja.",
  "url": `${BASE}/katzenberg`,
  "telephone": "+48663746095",
  "email": "katzenberg.hodowla@gmail.com",
  "address": { "@type": "PostalAddress", "streetAddress": "Kałowice 89", "addressLocality": "Zawonia", "postalCode": "55-106", "addressRegion": "Dolnośląskie", "addressCountry": "PL" },
  "geo": { "@type": "GeoCoordinates", "latitude": 51.333333, "longitude": 17.1425 },
  "areaServed": ["Dolnośląskie", "Wrocław", "Polska"],
  "sameAs": ["https://www.facebook.com/profile.php?id=61593187978675"],
  "image": `${BASE}/logo_katzenberg_new.png`
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Wolfe vom Katzenberg",
  "url": `${BASE}/katzenberg`,
  "logo": `${BASE}/logo_katzenberg_new.png`,
  "contactPoint": { "@type": "ContactPoint", "telephone": "+48663746095", "contactType": "customer service", "availableLanguage": "Polish" }
};

export const animalBreederSchema = {
  "@context": "https://schema.org",
  "@type": "AnimalShelter",
  "name": "Wolfe vom Katzenberg — Hodowla Owczarka Niemieckiego",
  "description": "Profesjonalna hodowla owczarków niemieckich zarejestrowana w ZKwP pod patronatem FCI. Szczenięta z rodowodem, badania HD/ED/DNA, socjalizacja.",
  "url": `${BASE}/katzenberg`,
  "telephone": "+48663746095",
  "email": "katzenberg.hodowla@gmail.com",
  "address": { "@type": "PostalAddress", "streetAddress": "Kałowice 89", "addressLocality": "Zawonia", "postalCode": "55-106", "addressCountry": "PL", "addressRegion": "Dolnośląskie" },
  "geo": { "@type": "GeoCoordinates", "latitude": 51.333333, "longitude": 17.1425 },
  "areaServed": ["Dolnośląskie", "Wrocław", "Polska"],
  "sameAs": ["https://www.facebook.com/profile.php?id=61593187978675"],
  "image": `${BASE}/logo_katzenberg_new.png`,
  "priceRange": "$$",
  "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "08:00", "closes": "20:00" }
};

export const reviewSchema = (reviews) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Wolfe vom Katzenberg",
  "url": `${BASE}/katzenberg`,
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5",
    "bestRating": "5",
    "worstRating": "1",
    "ratingCount": String(reviews?.length || 5)
  },
  "review": (reviews || []).slice(0, 5).map(r => ({
    "@type": "Review",
    "author": { "@type": "Person", "name": r.name || "Właściciel szczenięcia" },
    "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
    "reviewBody": r.text || ""
  }))
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    "item": `${BASE}${item.href}`
  }))
});

export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": items.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a }
  }))
});

export default KennelSEO;
